import { useReducer, useEffect, useCallback, type Reducer } from 'react'

type State<O> = {
  response: O | null
  meta: {
    isLoading: boolean
    isLoaded: boolean
    isError: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any
  }
}

type ResetAction = {
  type: 'RESET'
}

type RequestedAction = {
  type: 'REQUESTED'
}

type SuccessAction<O> = {
  type: 'SUCCESS'
  payload: O | null
}

type FailureAction = {
  type: 'FAILURE'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
}

type Action<O> = ResetAction | RequestedAction | SuccessAction<O> | FailureAction

const initial = {
  response: null,
  meta: {
    isLoaded: false,
    isLoading: false,
    isError: false,
  },
}

function reducer<O>(state: State<O>, action: Action<O>): State<O> {
  switch (action.type) {
    case 'RESET':
      return initial
    case 'REQUESTED':
      return {
        ...state,
        meta: {
          isLoaded: state.meta.isLoaded,
          isLoading: true,
          isError: false,
        },
      }
    case 'SUCCESS':
      return {
        response: action.payload,
        meta: {
          isLoaded: true,
          isLoading: false,
          isError: false,
        },
      }
    case 'FAILURE':
      return {
        ...state,
        response: null,
        meta: {
          isLoaded: true,
          isLoading: action.payload.code === 'ERR_CANCELED',
          isError: true,
          error: action.payload,
        },
      }
    default:
      return state
  }
}

export type AsyncCallResponse<T> = [
  T,
  {
    isLoading: boolean
    isLoaded: boolean
    isError: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error?: any
    refresh: () => Promise<unknown>
  },
]

export function useAsyncCall<A>(fn?: () => Promise<A>): AsyncCallResponse<A | null> {
  const [state, dispatch] = useReducer(reducer as Reducer<State<A>, Action<A>>, initial)

  const go = useCallback(() => {
    if (!fn) {
      return Promise.resolve(dispatch({ type: 'RESET' }))
    }
    dispatch({ type: 'REQUESTED' })

    return fn()
      .then((response) => {
        dispatch({ type: 'SUCCESS', payload: response })
      })
      .catch((error) => {
        dispatch({ type: 'FAILURE', payload: error })
      })
  }, [fn])

  const refresh = useCallback(() => go(), [go])

  useEffect(() => {
    go()
  }, [go])

  return [
    state.response,
    {
      ...state.meta,
      refresh,
    },
  ]
}
